import { useState, type ReactElement } from 'react'
import { isEmpty, isNotEmpty, type UniversalSearchItem } from '@terros/common'
import { Modal, ScrollArea, Stack, Text, TextInput } from '@mantine/core'
import { useDebounce, useToastError } from '@hooks'
import { Size, useTranslation, useUniversalSearchQuery } from '@front/model'
import { UserInfo } from '@components'
import type { DashboardScopeTarget, DashboardUserScopeTarget } from './types'
import { DashboardScopeOptionButton } from './DashboardScopeOptionButton'

type DashboardUserSearchModalProps = {
  opened: boolean
  onClose: () => void
  onSelect: (target: DashboardScopeTarget) => void
}

export const DashboardUserSearchModal = ({
  opened,
  onClose,
  onSelect,
}: DashboardUserSearchModalProps): ReactElement => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search.trim(), 500)
  const {
    data: results = [],
    isLoading,
    error,
  } = useUniversalSearchQuery({ query: debouncedSearch, scopes: ['User'], archived: false }, opened)
  useToastError(error)
  const targets = results.map(toUserDashboardScopeTarget).filter(isNotEmpty)

  const handleClose = (): void => {
    setSearch('')
    onClose()
  }

  const handleSelect = (target: DashboardScopeTarget): void => {
    setSearch('')
    onSelect(target)
  }

  return (
    <Modal opened={opened} title={t('Browse Users')} onClose={handleClose}>
      <Stack gap='sm'>
        <TextInput
          autoFocus
          value={search}
          placeholder={t('Search users')}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <ScrollArea.Autosize mah={360}>
          <Stack gap={Size.gap.list}>
            {targets.map((target) => (
              <DashboardScopeOptionButton key={target.entityId} onClick={() => handleSelect(target)}>
                <UserInfo linked={false} userId={target.entityId} size='sm' />
              </DashboardScopeOptionButton>
            ))}
            {isEmpty(search) && <Text c='dimmed'>{t('Search for a user')}</Text>}
            {isNotEmpty(search) && isLoading && <Text c='dimmed'>{t('Searching...')}</Text>}
            {isNotEmpty(search) && !isLoading && isEmpty(targets) && <Text c='dimmed'>{t('No results found')}</Text>}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Modal>
  )
}

function toUserDashboardScopeTarget(item: UniversalSearchItem): DashboardUserScopeTarget | undefined {
  if (item.type === 'User') return { entityId: item.userId, name: item.description, kind: 'user' }
  return undefined
}
